/**
 * CampaignMap — the compact 2x2 from the approved handoff (`Tu tráfico.dc.html`, 3a/3b).
 *
 * Horizontal = how much of the traffic stays and looks; vertical = how much it buys. The
 * horizontal axis is ABSOLUTE: 0–100% engagement, split at 50% ("most visitors looked" vs "most
 * bounced"), so a bubble's place reads as its real share. The vertical axis stays relative —
 * split at the store's session-weighted average and stretched (see axisScale) — because
 * conversion rates are too small to read on an absolute scale and the floor must mean
 * "nobody bought". Bubble size is visits, i.e. how much a row matters, and
 * deliberately never enters the judgement (ACQUISITION-VALUE-SPEC §1.5).
 *
 * The vertical axis is ADAPTIVE, for a reason that outlives the rollout: a row's quadrant
 * is placed on its CONVERSION RATE (`compareArms` — revenue isn't Bernoulli and has no
 * posterior), so plotting height by value-per-visit lets a green bubble sit low. Height
 * therefore follows conversion rate unless every row has a price, in which case the richer
 * value-per-visit reading is used — it folds ticket in, and colour and height still agree
 * because the two orderings coincide when ticket is roughly flat.
 */

import { useEffect, useRef, useState } from 'react'
import type { TrafficCampaignRow, TrafficChannelGroup } from '@/api/traffic'
import { QUADRANT, boughtPer100, campaignChartLabels, isOpaqueCampaignId, channelColor } from './quadrants'
import { copyText } from './CopyCampaign'
import { fmtInt, fmtMoney } from '@/lib/formatEsAr'

interface Props {
  rows: TrafficCampaignRow[]
  /** Desktop labels every bubble; mobile only the standouts, or the grid turns to soup. */
  showAllNames?: boolean
  width?: number
  height?: number
}

/** Below this there is no distribution to read — the list says it better. */
export const MAP_MIN_CAMPAIGNS = 2

/**
 * Plottable needs a horizontal position and a placement. The vertical one is derived from
 * whichever measure is available, so a row is never dropped for lacking a price.
 */
export function isMappable(row: TrafficCampaignRow): boolean {
  // Both coordinates are MEASURED, so any row that has them can be drawn truthfully. That is
  // the only requirement.
  //
  // This used to also demand a quadrant, which silently removed every half-placed row — and
  // `quadrantOf` returns `sin_datos` unless BOTH axes place. On GR that hid a 1,969-visit
  // campaign with ZERO conversions (value placed `below`, engagement indistinguishable from
  // its channel) while plotting 641-visit ones: Paid Social showed 6 of 23 rows. The most
  // actionable row in the store was missing because we could only say one thing about it
  // instead of two. The verdict is carried by the core dot, which `sin_datos` already
  // renders empty — so an unplaced row plots as a bubble with no core, which is exactly
  // what it is: a real position, no verdict yet.
  return row.engagement_rate != null && row.conversion_rate != null
}

/**
 * Fewest visits before a VERDICT about a campaign can be trusted.
 *
 * Derived from the chart's own precision: a share measured over n visits carries a standard
 * error of at most 0.5/√n, and both axes are shares. Keeping that error inside an eighth of
 * the axis gives 0.5/√n ≤ 0.125, i.e. n ≥ 16. Below it, a campaign plots at 0% or 100%
 * engagement purely by which single visitor it got.
 */
export const MIN_VISITS_FOR_VERDICT = 16

/**
 * Share of the store's campaign traffic that makes a campaign worth seeing on its own even
 * with nothing to say about it yet. "Where is my money going" is a question the chart has to
 * answer regardless of whether we can rank the answer.
 */
export const MATERIAL_SHARE = 0.01

/**
 * Does this row support a DECISION? That is the only question the chart exists to serve —
 * merchants and agencies read it to move budget, cut a campaign, or re-aim its targeting.
 *
 * Two independent ways to qualify, and the chart needs both arms:
 *
 *  - **Something to decide.** A verdict on at least one axis — better or worse than the rest
 *    of its channel. Size alone would have buried Luxo's three most actionable campaigns
 *    (761 visits / 13 conversions / above-above, and an 842-visit "entra y se va"), because
 *    they look immaterial next to two 13k–16k siblings in the same channel.
 *  - **Something at stake.** Enough of the store's campaign traffic that a change moves
 *    money, even unplaced. Verdict alone would have buried GR's 615- and 459-visit
 *    campaigns, which are indistinguishable from their channel but are real spend.
 *
 * The volume floor applies to the verdict arm only, because placements reach down to 2 visits
 * where they are prior-driven rather than measured. Volume decides whether a verdict is real;
 * the verdict decides whether there is anything to act on. Collapsing the two is what produced
 * every version of this bug.
 */
function isActionable(row: TrafficCampaignRow, storeCampaignVisits: number): boolean {
  const placed = row.placement.engagement !== 'unplaced' || row.placement.value !== 'unplaced'
  const soundVerdict = placed && row.sessions >= MIN_VISITS_FOR_VERDICT
  return soundVerdict || row.sessions >= storeCampaignVisits * MATERIAL_SHARE
}

/** Σ over a set of rows, with engagement keeping its own denominator. */
function mergeRows(channel: string, campaign: string, rows: TrafficCampaignRow[]): TrafficCampaignRow {
  let sessions = 0
  let conversions = 0
  let engaged = 0
  let engagedSessions = 0
  let revenue = 0
  let pricedSessions = 0
  for (const r of rows) {
    sessions += r.sessions
    conversions += r.conversions
    if (r.engaged != null) {
      engaged += r.engaged
      engagedSessions += r.sessions
    }
    if (r.value_per_visit != null) {
      revenue += r.value_per_visit * r.sessions
      pricedSessions += r.sessions
    }
  }
  return {
    channel,
    campaign,
    medium: null,
    sessions,
    conversions,
    engaged: engagedSessions > 0 ? engaged : undefined,
    // A merged bag is read against nothing — it is an accounting row, not a candidate for
    // action — so it carries no placement and gets no verdict core.
    value_per_visit: pricedSessions === sessions && sessions > 0 ? revenue / sessions : null,
    engagement_rate: engagedSessions > 0 ? engaged / engagedSessions : null,
    conversion_rate: sessions > 0 ? conversions / sessions : null,
    placement: { engagement: 'unplaced', value: 'unplaced' },
    quadrant: 'sin_datos',
    stability: 'sin_datos',
    is_rest: true,
  }
}

/**
 * The rows the map plots, per channel.
 *
 * Two guarantees, and they pull in opposite directions:
 *  - no campaign is dropped — every visit is somewhere on the chart;
 *  - no campaign gets its own bubble unless there is a decision to make about it.
 *
 * Both are satisfied by CONSOLIDATING rather than hiding: campaigns that fail `isActionable`
 * are folded into their channel's "El resto de …" bubble, which already exists to hold
 * traffic we can't individualise (untagged visits, and the tail the producer folded past its
 * top-N cap). Their visits stay counted, stay in the right channel, and stay in the tooltip's
 * breakdown — they just stop claiming a position nobody could act on.
 *
 * Folding is per CHANNEL, never per quadrant. A quadrant bag would group campaigns by the
 * noisiest thing about them: GR's 19 tiny campaigns span 0%–100% engagement individually and
 * 12 of them sit at exactly 0% or 100%, yet pooled they sit at 53% — the store average.
 * Binning on that coordinate and then aggregating would draw a confident bubble in the
 * "nobody looks" corner that is an artifact of which single visitor each campaign got.
 *
 * If a channel contributes no bubble at all, fall back to the channel taken whole, so a
 * channel with traffic can never vanish.
 */
export function mapRows(channels: TrafficChannelGroup[]): TrafficCampaignRow[] {
  // Materiality is measured against the campaign traffic we can actually name, store-wide —
  // not against the channel, which lets one dominant campaign make its siblings look
  // disposable, and not against all sessions, which untagged channels would swamp.
  let storeCampaignVisits = 0
  for (const c of channels) {
    for (const r of c.campaigns) if (!r.is_rest && !r.is_channel_rollup) storeCampaignVisits += r.sessions
  }

  return channels.flatMap((c) => {
    const rows = c.campaigns.filter(isMappable)
    const own = rows.filter((r) => r.is_rest || isActionable(r, storeCampaignVisits))
    const tiny = rows.filter((r) => !r.is_rest && !isActionable(r, storeCampaignVisits))

    let out = own
    if (tiny.length > 0) {
      const rest = own.find((r) => r.is_rest)
      const merged = mergeRows(c.channel, rest?.campaign ?? `El resto de ${c.channel}`, rest ? [rest, ...tiny] : tiny)
      merged.rest_untagged_sessions = rest?.rest_untagged_sessions
      merged.rest_folded_sessions = rest?.rest_folded_sessions
      merged.rest_small_campaigns = tiny.length
      merged.rest_small_sessions = tiny.reduce((s, r) => s + r.sessions, 0)
      merged.placed_vs = rest?.placed_vs
      out = [...own.filter((r) => !r.is_rest), merged]
    }

    if (out.length > 0) return out
    return c.rollup && isMappable(c.rollup) ? [c.rollup] : []
  })
}

/**
 * Whether to render the surrounding card at all. Deliberately permissive: the chart stays
 * up while the data fills in (a flat axis is labelled, not hidden), so this only guards
 * against an empty plot.
 */
export function isMapMeaningful(rows: TrafficCampaignRow[]): boolean {
  return rows.filter(isMappable).length >= MAP_MIN_CAMPAIGNS
}

/** Session-weighted store average — the point the axes cross at. */
function weightedMean(rows: TrafficCampaignRow[], pick: (r: TrafficCampaignRow) => number | null) {
  let num = 0
  let den = 0
  for (const r of rows) {
    const v = pick(r)
    if (v == null) continue
    num += v * r.sessions
    den += r.sessions
  }
  return den > 0 ? num / den : 0
}

/**
 * Piecewise scale: the two halves are stretched independently so the WORST row lands on the
 * bottom/left edge, the average lands on the crossing line, and the best lands on the
 * top/right edge.
 *
 * A single symmetric scale around the mean got this badly wrong in the common case. When
 * most campaigns convert at 0 and one converts at 1%, the mean sits near zero — so every
 * zero-conversion campaign plotted ON the crossing line, i.e. reading as "average" when it
 * is the worst possible result. Stretching each side to its own extreme puts "nobody
 * bought" at the floor, where it belongs.
 */
function axisScale(values: number[], center: number, half: number) {
  const below = center - Math.min(...values)
  const above = Math.max(...values) - center
  const fn = (v: number) =>
    v >= center
      ? above > 0
        ? ((v - center) / above) * half
        : 0
      : below > 0
        ? -((center - v) / below) * half
        : 0
  fn.spread = Math.max(above, below)
  return fn as ((v: number) => number) & { spread: number }
}

export interface BubbleNode<T> {
  item: T
  /** True position — what the data says. Never mutated. */
  x0: number
  y0: number
  /** Drawn position: x0/y0 plus at most one radius of nudge. */
  x: number
  y: number
  r: number
  /** Pin the vertical: used for "sold nothing", where the floor IS the statement. */
  lockY: boolean
}

interface RelaxBounds {
  /** The two dividers. A bubble may never be nudged across either one. */
  crossX: number
  crossY: number
  minX: number
  maxX: number
  minY: number
  maxY: number
}

/**
 * Nudge overlapping bubbles apart, in place.
 *
 * Now that every campaign plots, exact collisions are the normal case rather than the
 * exception: every zero-conversion campaign shares the same floor, and a channel's campaigns
 * cluster around its own engagement rate. Three bubbles sat exactly on top of each other
 * before this — and a bubble hidden underneath another is a dropped campaign by another route.
 *
 * It is a NUDGE, not a layout. Three rules keep the chart honest while it declutters:
 *  - a bubble moves only as far as it must to stop being hidden (see the leash below),
 *  - never across either divider, so it cannot land in a quadrant it did not earn,
 *  - never vertically at all when the row sold nothing — the floor is the statement, so
 *    those rows spread sideways only.
 *
 * Within a single collision the push is shared by area, so the bigger bubble gives way less;
 * across the whole plot a small bubble stays nearer its true spot in pixels, because its
 * leash is shorter.
 *
 * Deterministic: same input, same output, so a re-render never reshuffles the plot.
 */
export function relaxBubbles<T>(nodes: BubbleNode<T>[], b: RelaxBounds, passes = 60): void {
  const GAP = 1.5

  /**
   * How far each bubble may stray.
   *
   * One radius was the first attempt and it is provably too short: three coincident r=10
   * bubbles need their centres ~11px apart to stop covering each other, which a ±10 leash
   * cannot deliver — and a pile of zero-conversion campaigns on the floor is the common case,
   * not a corner one. So the leash is the radius of the smallest ring that fits the pile:
   * k circles of radius r sit clear of each other on a ring of radius (r + gap/2)/sin(π/k).
   * A bubble that shares its spot with nobody keeps the tight leash; only a pile earns room,
   * and only as much as the pile needs.
   */
  const maxLeash = (b.maxX - b.minX) * 0.18
  const leashes = nodes.map((n, i) => {
    let pile = 1
    for (let j = 0; j < nodes.length; j++) {
      if (j === i) continue
      const m = nodes[j]
      if (Math.hypot(m.x0 - n.x0, m.y0 - n.y0) < n.r + m.r + GAP) pile++
    }
    const base = Math.max(8, n.r)
    if (pile < 2) return base
    const k = Math.min(pile, 12)
    // A y-locked bubble can only escape along ONE axis, so it needs the half-span of a row
    // of k circles — strictly more than the ring a free bubble can spread onto.
    const need = n.lockY
      ? ((k - 1) * (2 * n.r + GAP)) / 2
      : (n.r + GAP / 2) / Math.sin(Math.PI / k)
    return Math.min(Math.max(base, need), maxLeash)
  })

  const clamp = (n: BubbleNode<T>, leash: number) => {
    if (n.lockY) n.y = n.y0
    const dx = n.x - n.x0
    const dy = n.y - n.y0
    const dist = Math.hypot(dx, dy)
    if (dist > leash) {
      n.x = n.x0 + (dx / dist) * leash
      n.y = n.y0 + (dy / dist) * leash
    }
    n.x = n.x0 >= b.crossX ? Math.max(b.crossX, n.x) : Math.min(b.crossX, n.x)
    if (!n.lockY) n.y = n.y0 <= b.crossY ? Math.min(b.crossY, n.y) : Math.max(b.crossY, n.y)
    n.x = Math.min(b.maxX - n.r, Math.max(b.minX + n.r, n.x))
    if (!n.lockY) n.y = Math.min(b.maxY - n.r, Math.max(b.minY + n.r, n.y))
  }

  for (let pass = 0; pass < passes; pass++) {
    let moved = false
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i]
        const c = nodes[j]
        let dx = c.x - a.x
        let dy = c.y - a.y
        let d = Math.hypot(dx, dy)
        const min = a.r + c.r + GAP
        if (d >= min) continue
        if (d < 0.001) {
          // Exactly coincident: separate along a fixed axis so the render stays stable.
          dx = j % 2 === 0 ? 1 : -1
          dy = 0.35
          d = Math.hypot(dx, dy)
        }
        const push = min - d
        const ma = a.r * a.r
        const mc = c.r * c.r
        const ux = dx / d
        const uy = dy / d
        a.x -= ux * ((push * mc) / (ma + mc))
        a.y -= uy * ((push * mc) / (ma + mc))
        c.x += ux * ((push * ma) / (ma + mc))
        c.y += uy * ((push * ma) / (ma + mc))
        moved = true
      }
    }
    for (let i = 0; i < nodes.length; i++) clamp(nodes[i], leashes[i])
    if (!moved) break
  }
}

/**
 * Engagement is a share of visits, so it has a natural absolute scale: 0% at the left edge,
 * 100% at the right, linear in between.
 *
 * It used `axisScale` like the vertical axis did, and that stretch is wrong here. It pins the
 * WORST row to the left edge whatever its value, so a channel where half the visitors stayed
 * sat in the far corner next to "nobody looked" — position claimed a failure the number didn't
 * support. The vertical axis keeps the stretch on purpose: there the floor has to mean "nobody
 * bought", and conversion rates are too small to read on a 0–100% scale.
 */
function absoluteShareScale(half: number) {
  return (v: number) => (Math.min(1, Math.max(0, v)) - 0.5) * 2 * half
}

const LABEL_CHAR_PX = 5.6 // 10.5px sans — close enough to reserve space with

/**
 * What is actually inside the "el resto de {canal}" bag.
 *
 * We can't list the campaigns — untagged visits have no name to list, and the folded tail's
 * names are summed away by the producer before the reader sees them. But we CAN say how
 * much of the bag is each, which is the difference between "some traffic we lumped
 * together" and a number the merchant can act on: a large untagged share is a UTM problem
 * they can hand to whoever runs the ads, a large folded share is just a long tail.
 */
function RestBreakdown({ row }: { row: TrafficCampaignRow }) {
  const untagged = row.rest_untagged_sessions
  const folded = row.rest_folded_sessions
  const small = row.rest_small_campaigns
  const parts: string[] = []
  if (untagged) parts.push(`${fmtInt(untagged)} sin campaña identificada`)
  if (folded) parts.push(`${fmtInt(folded)} de campañas más chicas`)
  // The chart's own fold. Named separately from the producer's tail because this one IS
  // recoverable: the campaigns exist by name in the list below, they just have too few
  // visits to stand on the chart.
  if (small && row.rest_small_sessions) {
    parts.push(
      `${fmtInt(row.rest_small_sessions)} de ${fmtInt(small)} ${small === 1 ? 'campaña con pocas visitas' : 'campañas con pocas visitas'}`
    )
  }
  if (parts.length === 0) return null
  return (
    <div className="mt-1.5 text-[.6875rem] leading-snug text-[#888]">{parts.join(' · ')}</div>
  )
}

/**
 * Where the campaign sends people, in full — never truncated.
 *
 * This is the half of the picture the axes can't show. At Beauty Web the best campaign in the
 * store (87% engagement, 7.4% conversion) sends 92% of its traffic to one landing page, and
 * the worst (6% engagement, no sales) sends half of it to the home page. "Engages badly" is a
 * finding; "lands everyone on the home page" is something the agency can fix this afternoon.
 *
 * Paths are shown whole because a truncated URL can't be matched against an ad's link, which
 * is the entire point of showing it. Shares deliberately don't sum to 100% — see
 * `landing_coverage` — and the remainder is named rather than hidden.
 */
function LandingList({ row }: { row: TrafficCampaignRow }) {
  const landings = row.landings
  if (!landings || landings.length === 0) return null
  const listed = landings.reduce((s, l) => s + (l.share ?? 0), 0)
  const rest = 1 - listed
  return (
    <div className="mt-1.5 border-t border-[#f0f0f0] pt-1.5">
      <div className="text-[.6875rem] leading-snug text-[#888]">Dónde aterrizan</div>
      {landings.map((l) => (
        <div key={l.path} className="mt-0.5 flex items-start gap-1.5 text-[.6875rem] leading-snug">
          <span className="shrink-0 tabular-nums text-[#5d5d5d]">
            {l.share != null ? `${Math.round(l.share * 100)}%` : '—'}
          </span>
          {/* `break-all`: these are URLs, so they wrap mid-token rather than overflow. */}
          <span className="break-all text-[#0a0a0a]">{l.path}</span>
        </div>
      ))}
      {rest > 0.05 && (
        <div className="mt-0.5 text-[.6875rem] leading-snug text-[#888]">
          {Math.round(rest * 100)}% en otras páginas
        </div>
      )}
    </div>
  )
}

/**
 * Everything the bubble is standing for. Most bubbles carry no label (opaque IDs, or
 * crowded plots), so without this a merchant can see a shape and not know what it is —
 * which is the one thing a chart must never do.
 */
function Tooltip({
  row,
  xPct,
  yPct,
  copied,
}: {
  row: TrafficCampaignRow
  xPct: number
  yPct: number
  copied: boolean
}) {
  const meta = QUADRANT[row.quadrant]
  const looked = row.engagement_rate != null ? Math.round(row.engagement_rate * 100) : null
  const bought = row.conversion_rate != null ? boughtPer100(row.conversion_rate) : null
  // Flip to the other side near the edges so the card never leaves the card.
  const flipX = xPct > 58
  const flipY = yPct < 34
  return (
    <div
      className="pointer-events-none absolute z-10 w-[268px] rounded-lg border border-[#e7e7e7] bg-white p-2.5 shadow-md"
      style={{
        left: `${xPct}%`,
        top: `${yPct}%`,
        transform: `translate(${flipX ? 'calc(-100% - 14px)' : '14px'}, ${flipY ? '8px' : 'calc(-100% - 8px)'})`,
      }}
    >
      <div className="flex items-start gap-1.5">
        <span className="text-sm leading-tight">{meta.emoji}</span>
        <span className="text-[.8125rem] font-medium leading-tight text-[#0a0a0a]">{meta.label}</span>
      </div>
      <div className="mt-1 break-words text-[.6875rem] leading-snug text-[#888]">
        {row.is_rest ? row.campaign : row.campaign}
      </div>
      <div className="mt-1.5 flex flex-col gap-0.5 text-[.75rem] tabular-nums text-[#0a0a0a]">
        <span>{fmtInt(row.sessions)} visitas · {row.channel}</span>
        {looked != null && <span>{looked} de cada 100 se quedan mirando</span>}
        {bought && (
          <span>
            {bought.count.charAt(0).toUpperCase() + bought.count.slice(1)} de cada 100 {bought.verb}
          </span>
        )}
        {row.value_per_visit != null && <span>{fmtMoney(row.value_per_visit)} por visita</span>}
      </div>
      {row.is_rest && <RestBreakdown row={row} />}
      {/* The roll-up stands for the whole channel, so say what it is summing — otherwise a
          bubble labelled "Todo Email" reads as one more campaign that happens to be big. */}
      {row.is_channel_rollup && row.rollup_campaigns != null && row.rollup_campaigns > 0 && (
        <div className="mt-1.5 text-[.6875rem] leading-snug text-[#888]">
          Suma de {fmtInt(row.rollup_campaigns)}{' '}
          {row.rollup_campaigns === 1 ? 'campaña, individualmente' : 'campañas, individualmente'} muy
          chicas para medir por separado
        </div>
      )}
      {/* This row is the whole channel, so it is read against the rest of the store rather
          than against sibling campaigns. Say which comparison produced the colour. */}
      {row.placed_vs === 'store' && (
        <div className="mt-1 text-[.6875rem] leading-snug text-[#888]">
          Comparado con el resto de tu tienda.
        </div>
      )}
      <LandingList row={row} />
      {/* A click copies the identifier. Advertised on the card rather than left to be
          discovered, and confirmed in the same place so the feedback lands where the reader
          is already looking. Bags carry no identifier worth pasting anywhere. */}
      {!row.is_rest && !row.is_channel_rollup && (
        <div className={`mt-1 text-[.6875rem] leading-snug ${copied ? 'text-[#00935b]' : 'text-[#888]'}`}>
          {copied ? '✓ Nombre copiado' : 'Clic para copiar el nombre'}
        </div>
      )}
    </div>
  )
}

export function CampaignMap({ rows, showAllNames = false, width = 368, height = 230 }: Props) {
  const [active, setActive] = useState<string | null>(null)
  // Which bubble was just copied, so the tooltip can confirm it. Keyed, not boolean: the
  // confirmation belongs to one bubble and must not follow the pointer to the next.
  const [copied, setCopied] = useState<string | null>(null)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current) }, [])

  const plotted = rows.filter(isMappable)
  if (plotted.length < MAP_MIN_CAMPAIGNS) return null

  // Price everywhere → use it. Otherwise conversion rate, which is always measured and is
  // what the quadrant colour is placed on.
  const priced = plotted.every((r) => r.value_per_visit != null)
  const yValue = (r: TrafficCampaignRow) =>
    priced ? r.value_per_visit! : r.conversion_rate!

  const cx0 = width / 2
  const cy0 = (height - 30) / 2 + 4
  const halfX = width / 2 - 46
  const halfY = (height - 30) / 2 - 26

  const centerY = weightedMean(plotted, yValue)

  const xOf = absoluteShareScale(halfX)
  const yOf = axisScale(plotted.map(yValue), centerY, halfY)
  // The left/right split is absolute too: 50% engagement, i.e. "most visitors looked" vs
  // "most bounced". Splitting at the store average (67% on Luxo) put channels where two out of
  // three visitors stayed in the red "few look" corner — relative to the store, true; as a
  // claim about the traffic, wrong. The 50% tick labels the line, so it needs no caption.
  const crossX = cx0 + xOf(0.5)

  /**
   * Radius by visits. The old `11 + 9·√(s/max)` floor meant everything below ~a third of
   * the leader collapsed into the same ~12px dot — the caption promised size meant visits
   * and it visibly didn't. Anchoring to the observed min..max instead of 0..max spends the
   * whole 6..26px range on the spread that actually exists. √ keeps AREA roughly
   * proportional to visits, which is how a circle is read.
   */
  const sizes = plotted.map((r) => r.sessions)
  const minSessions = Math.min(...sizes)
  const maxSessions = Math.max(...sizes)
  /**
   * …and scaled to the canvas. The 6–26px range was fixed regardless of chart size, which
   * was survivable while only a handful of rows plotted and is not now that every campaign
   * does: the same 41 bubbles cover 8% of the consultant chart and 33% of the Home one, where
   * they cannot be untangled at any displacement (19 bubbles stayed hidden under each other).
   * Scaling by the square root of the area holds ink density roughly constant, which takes
   * Home from 19 hidden to 2.
   */
  const sizeScale = Math.min(1, Math.max(0.55, Math.sqrt((width * height) / (860 * 380))))
  const radiusOf = (sessions: number) => {
    if (maxSessions <= minSessions) return 14 * sizeScale
    return (6 + 20 * Math.sqrt((sessions - minSessions) / (maxSessions - minSessions))) * sizeScale
  }

  // The two rows a merchant should recognise without reading every label. Drawn only from
  // rows that earned a placement: now that every campaign plots, the lowest-engagement bubble
  // is often a 1-visit row at 0%, and crowning that "the one to look at" on a chart where it
  // may be the ONLY label would be worse than labelling nothing.
  const notable = plotted.filter((r) => r.quadrant !== 'sin_datos')
  const pool = notable.length > 0 ? notable : plotted
  const best = pool.reduce((a, b) => (yValue(b) > yValue(a) ? b : a))
  const worst = pool.reduce((a, b) => (b.engagement_rate! < a.engagement_rate! ? b : a))

  const bottomRule = cy0 + halfY + 8

  const nodes: BubbleNode<TrafficCampaignRow>[] = plotted.map((row) => {
    const x0 = cx0 + xOf(row.engagement_rate!)
    const y0 = cy0 - yOf(yValue(row))
    return { item: row, x0, y0, x: x0, y: y0, r: radiusOf(row.sessions), lockY: yValue(row) === 0 }
  })
  relaxBubbles(nodes, {
    crossX,
    crossY: cy0,
    minX: 8,
    maxX: width - 8,
    minY: 8,
    maxY: bottomRule,
  })

  const posOf = new Map(nodes.map((n) => [n.item, n]))
  const at = (row: TrafficCampaignRow) => posOf.get(row)!
  const nudged = nodes.some((n) => Math.hypot(n.x - n.x0, n.y - n.y0) > 0.5)

  // Labels are a scarce resource on a 368px plot. Three rules keep them readable:
  //  1. Skip any label whose BOX would touch one already placed. Bubbles cluster (that IS
  //     the finding), so a distance check on centres alone still let long labels collide.
  //  2. Standouts get first refusal, so the ones worth reading win the space.
  //  3. Names before IDs, size before smallness — see `ordered` below.
  const placed: Array<{ x1: number; x2: number; y: number }> = []

  // Every bubble's box, so a label can't be written straight through a circle. Checking
  // labels against each other was not enough: with several channels the text ran across
  // neighbouring bubbles and became unreadable even though no two labels touched.
  const bubbleBoxes = nodes.map((n) => ({
    x1: n.x - n.r, x2: n.x + n.r, y1: n.y - n.r, y2: n.y + n.r,
  }))

  const fits = (x1: number, x2: number, y: number) => {
    if (placed.some((p) => Math.abs(p.y - y) < 13 && x1 < p.x2 + 6 && p.x1 - 6 < x2)) return false
    // Text baseline sits ~8px above / 3px below its own line box.
    return !bubbleBoxes.some((b) => x1 < b.x2 + 2 && b.x1 - 2 < x2 && y + 3 > b.y1 && b.y2 > y - 8)
  }

  const axisY = cy0 + halfY + 22

  /**
   * Who gets a label, in order of first refusal:
   *   1. the two standouts,
   *   2. campaigns with a human-readable name, biggest first,
   *   3. campaigns identified only by an ID, biggest first.
   *
   * Rule 3 is the change: an ID used to be skipped outright, which left the largest bubble
   * on the chart anonymous whenever the account templates `{{campaign.id}}`. Names still win
   * the scarce space — that is rule 2 — but a big ID now beats a small name, because size is
   * what the merchant is scanning for. Nothing is excluded by a volume cutoff: the collision
   * test below is the rationing, so small bubbles simply lose when there is no room.
   */
  const bySize = [...plotted].sort((a, b) => b.sessions - a.sessions)
  const isStandout = (r: TrafficCampaignRow) => r === best || r === worst
  const ordered = [
    ...bySize.filter(isStandout),
    ...bySize.filter((r) => !isStandout(r) && !isOpaqueCampaignId(r.campaign)),
    ...bySize.filter((r) => !isStandout(r) && isOpaqueCampaignId(r.campaign)),
  ]
  const labelText = campaignChartLabels(plotted.map((r) => r.campaign))
  type Anchor = 'start' | 'middle' | 'end'
  const labelFor = new Map<TrafficCampaignRow, { text: string; x: number; y: number; anchor: Anchor }>()

  for (const row of ordered) {
    if (!showAllNames && row !== best && row !== worst) continue
    const { x: cx, y: cy, r } = at(row)
    const text = labelText.get(row.campaign) ?? row.campaign
    const w = text.length * LABEL_CHAR_PX
    const anchor: Anchor = cx > width - 74 ? 'end' : cx < 74 ? 'start' : 'middle'
    const x = anchor === 'end' ? width - 8 : anchor === 'start' ? 8 : cx
    const x1 = anchor === 'end' ? x - w : anchor === 'start' ? x : x - w / 2
    const y = cy + r + 14
    if (!fits(x1, x1 + w, y)) continue
    placed.push({ x1, x2: x1 + w, y })
    labelFor.set(row, { text, x, y, anchor })
  }

  const keyOf = (r: TrafficCampaignRow) => `${r.channel}::${r.campaign}`
  const activeRow = plotted.find((r) => keyOf(r) === active) || null

  const sessionsByChannel = new Map<string, number>()
  for (const r of plotted) sessionsByChannel.set(r.channel, (sessionsByChannel.get(r.channel) || 0) + r.sessions)
  const channelsPresent = [...sessionsByChannel.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([ch]) => ch)

  return (
    <div className="flex flex-col gap-1.5">
      {/* The tooltip anchors to the PLOT box, not the whole card — the caption below would
          otherwise skew the percentage positioning. */}
      <div className="relative">
      <svg
        onMouseLeave={() => setActive(null)}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        className="block"
        role="img"
        aria-label={
          priced
            ? 'Mapa de campañas: qué tanto miran contra cuánto vale cada visita'
            : 'Mapa de campañas: qué tanto miran contra qué tanto compran'
        }
      >
        {/*
          Quadrant washes. Position alone told a merchant WHERE a campaign sat but not
          whether that was good or bad — the chart was legible and inert. Tinting the two
          diagonal corners makes the verdict readable before any label is read: bottom-left
          (few look, few buy) is the one worth acting on, top-right is the one to protect.
          The two ambiguous quadrants stay neutral on purpose — "engages but doesn't buy"
          is genuinely undecided until the objective is known.
        */}
        <rect
          x={8}
          y={cy0}
          width={Math.max(0, crossX - 8)}
          height={Math.max(0, bottomRule - cy0)}
          fill="#b91c1c"
          fillOpacity={0.05}
        />
        <rect
          x={crossX}
          y={8}
          width={Math.max(0, width - 8 - crossX)}
          height={Math.max(0, cy0 - 8)}
          fill="#00935b"
          fillOpacity={0.05}
        />
        <line x1={crossX} y1={8} x2={crossX} y2={bottomRule} stroke="#f0f0f0" />
        <line x1={8} y1={cy0} x2={width - 8} y2={cy0} stroke="#f0f0f0" />

        {/* An absolute axis has to show its scale. Ticks go on their own row under the axis
            title because labels for bubbles sitting on the floor land on the title's row. */}
        {[0, 0.5, 1].map((t) => (
          <text
            key={t}
            x={cx0 + xOf(t)}
            y={axisY + 14}
            fontSize="10"
            fill="#b0b0b0"
            textAnchor="middle"
            className="tabular-nums"
          >
            {Math.round(t * 100)}%
          </text>
        ))}

        {plotted.map((row) => {
          const meta = QUADRANT[row.quadrant]
          const hue = channelColor(row.channel)
          const { x: cx, y: cy, r } = at(row)
          const label = labelFor.get(row)
          const key = keyOf(row)
          const isActive = key === active
          return (
            <g
              key={key}
              tabIndex={0}
              role="button"
              aria-label={`${row.campaign}: ${meta.label}`}
              className="cursor-pointer focus:outline-none"
              onMouseEnter={() => setActive(key)}
              onFocus={() => setActive(key)}
              onBlur={() => setActive(null)}
              // Touch has no hover: tap toggles the same card. On Home the map lives inside
              // a <Link>, so the tap must not also navigate away from the answer.
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setActive(isActive ? null : key)
                // Clicking a campaign also puts its identifier on the clipboard, because
                // that is the next thing the reader does with it: paste it into the ads
                // manager to act on what the chart just told them. Bags are skipped —
                // "El resto de Paid Social" is not a thing you can look up.
                if (!row.is_rest && !row.is_channel_rollup) {
                  void copyText(row.campaign).then((ok) => {
                    if (!ok) return
                    setCopied(key)
                    if (copyTimer.current) clearTimeout(copyTimer.current)
                    copyTimer.current = setTimeout(() => setCopied(null), 1600)
                  })
                }
              }}
            >
              {/* Generous invisible hit area — the smallest bubbles are ~6px across. */}
              <circle cx={cx} cy={cy} r={Math.max(r, 16)} fill="transparent" />
              {/*
                ONE mark per row. The quadrant emoji used to sit inside the circle, which
                read as a second marker nested in the first ("what's the outer one?") and
                said exactly what the position already said. Colour now carries the channel;
                the quadrant is the position, and the tooltip names it.
              */}
              <circle
                cx={cx}
                cy={cy}
                r={r}
                fill={hue.fill}
                stroke={hue.stroke}
                strokeWidth={isActive ? 2.5 : 1.5}
              />
              {/*
                The verdict, back at the centre — but as a small solid CORE rather than the
                old 12px emoji, which was big enough to read as a marker in its own right.
                Kept proportional and skipped on bubbles too small to hold it, so it never
                swallows the circle it sits in. `sin_datos` gets nothing: a grey core would
                claim a verdict we don't have.
              */}
              {r >= 9 && row.quadrant !== 'sin_datos' && (
                <circle
                  cx={cx}
                  cy={cy}
                  r={Math.min(5, Math.max(3, r * 0.3))}
                  fill={QUADRANT[row.quadrant].stroke}
                  className="pointer-events-none"
                />
              )}
              {label && (
                <text
                  x={label.x}
                  y={label.y}
                  fontSize="10.5"
                  fill="#5d5d5d"
                  fontWeight="500"
                  textAnchor={label.anchor}
                >
                  {label.text}
                </text>
              )}
            </g>
          )
        })}

        {/* Keeps the active bubble on top of its neighbours' hit areas. */}
        {activeRow && (
          <circle
            cx={at(activeRow).x}
            cy={at(activeRow).y}
            r={at(activeRow).r + 4}
            fill="none"
            stroke={channelColor(activeRow.channel).stroke}
            strokeOpacity={0.35}
            className="pointer-events-none"
          />
        )}

        <text x={10} y={18} fontSize="11" fill="#888">
          {priced ? '↑ Vale más por visita' : '↑ Compran más'}
        </text>
        <text x={width - 10} y={axisY} fontSize="11" fill="#888" textAnchor="end">
          Miran más →
        </text>
      </svg>

      {activeRow && (
        <Tooltip
          row={activeRow}
          xPct={(at(activeRow).x / width) * 100}
          yPct={(at(activeRow).y / height) * 100}
          copied={copied === keyOf(activeRow)}
        />
      )}
      </div>

      {/* Colour is only information if it can be decoded. Ordered by traffic so the channels
          that dominate the plot are named first. */}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {channelsPresent.map((ch) => {
          const hue = channelColor(ch)
          return (
            <span key={ch} className="flex items-center gap-1 text-[.6875rem] text-[#5d5d5d]">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: hue.fill, border: `1.5px solid ${hue.stroke}` }}
              />
              {ch}
            </span>
          )
        })}
      </div>

      <span className="text-xs leading-relaxed text-[#888]">
        {!priced
          ? 'Mientras completamos las ventas por campaña de toda la semana, la altura muestra cuántos compran. Cada círculo es una campaña: el color es el canal y el tamaño, la cantidad de visitas.'
          : yOf.spread === 0
            ? 'Todavía no hay diferencias en cuánto vale cada visita. Cada círculo es una campaña: el color es el canal y el tamaño, la cantidad de visitas.'
            : 'Cada círculo es una campaña: el color es el canal y el tamaño, la cantidad de visitas.'}
        {/* Said only when it actually happened. Once a position is approximate the reader is
            owed the warning — and if nothing moved, the warning would be a lie of its own. */}
        {nudged && ' Algunos círculos se separan apenas para no taparse: pasá el mouse para ver los números exactos.'}
      </span>
    </div>
  )
}
